/**
 * handleXmppStanza — routes incoming XMPP stanza data to the correct Redux action.
 *
 * @param {Object} data       - Parsed JSON payload from the XMPP stanza body.
 * @param {Object} context
 * @param {Function} context.dispatch        - Redux dispatch.
 * @param {Object}   context.props           - SocketClient props (user, users).
 * @param {string}   context.user_id         - Current user's ID.
 * @param {string}   context.user_fullname   - Current user's full name.
 * @param {Function} context.modifyMyMsg     - Message normaliser (async).
 * @param {Function} context.scrollToBottom  - UI scroll helper.
 * @param {Function} context.getONLYUSERSDATA - User lookup helper.
 * @param {Function} context.logout          - Hard logout (no mutation).
 * @param {Function} context.showVersionPopup - Version update handler.
 * @param {Function} context.soundNotification - Play notification sound.
 * @param {Function} context.slugify         - Convert title to URL slug.
 */
import {
    add_new_room,
    closeConversation,
    file_share_link_create,
    removeAttachment,
    removeMessage,
    sendOneMessage,
    setOneReplyData,
    set_online_users,
    set_callringpopup,
    setMuteDataOnProps,
    set_pin_unpin,
    updateArchiveConv,
    updateMessageStar,
    updateOneMsg,
    updateRoom,
    updateMsgCounter,
    updateFlagCounter,
    set_change_link_data,
    setMergeMsg,
    update_total_unread,
    set_kick_out_data,
    set_conv_calling_data,
    setPopup,
    updateConversationMap,
} from 'redux/messageSlice';
import {
    removeDiscussionAttachment,
    save_update_checklist,
    checklist_item_delete,
    set_deleted_task,
    set_new_task,
    update_active_discussion,
    updateDiscussionMsgStar,
    updateOneDiscussionMsg,
    setRemoveThisLine,
} from 'redux/taskSlice';
import { set_dashboardNotification } from 'redux/dashboardSlice';
import { actionTagThunk } from 'redux/actionThunk';

export async function handleXmppStanza(data, context) {
    const {
        dispatch,
        props,
        user_id,
        user_fullname,
        modifyMyMsg,
        scrollToBottom,
        getONLYUSERSDATA,
        logout,
        showVersionPopup,
        soundNotification,
        slugify,
    } = context;

    switch (data.xmpp_type) {
        case 'new_message': {
            const res_msg = await modifyMyMsg([data]);
            dispatch(sendOneMessage(res_msg[0]));
            soundNotification(props, res_msg[0]);
            setTimeout(() => scrollToBottom('main_msg_scroll_div'), 100);
            break;
        }
        case 'new_discussion': {
            const res_msg = await modifyMyMsg([data]);
            dispatch(update_active_discussion(res_msg[0]));
            soundNotification(props, res_msg[0]);
            break;
        }
        case 'logout_from_all':
            logout();
            break;
        case 'logout_from_here3': {
            const device_id = localStorage.getItem('xmpp_tokenid');
            if (device_id && data.device_id && device_id === data.device_id) {
                logout();
            }
            break;
        }
        case 'edit_message': {
            const res_msg = await modifyMyMsg([data]);
            dispatch(updateOneMsg({
                field: 'edit_history',
                call_duration: data.call_duration,
                msg_id: data.msg_id,
                conversation_id: data.conversation_id,
                is_reply_msg: data.is_reply_msg,
                edit_history: res_msg[0].edit_history,
                msg_body: res_msg[0].msg_body,
                msg: res_msg[0],
                url_base_title: res_msg[0].url_base_title,
                updatedAt: res_msg[0].updatedAt,
            }));
            break;
        }
        case 'file_url_title':
            dispatch(set_change_link_data(data));
            break;
        case 'merge_msg':
            dispatch(setMergeMsg(data));
            break;
        case 'flag_counter':
            dispatch(updateFlagCounter(data));
            break;
        case 'new_reply_message': {
            const res_msg = await modifyMyMsg([data]);
            dispatch(setOneReplyData(res_msg[0]));
            soundNotification(props, res_msg[0]);
            setTimeout(() => scrollToBottom('replyMsgFullContainer'), 100);
            break;
        }
        case 'room_archive':
            dispatch(updateArchiveConv(data));
            break;
        case 'close_room':
            dispatch(closeConversation(data));
            break;
        case 'edit_reply_message': {
            const res_msg = await modifyMyMsg([data]);
            dispatch(updateOneMsg({
                field: 'edit_history',
                msg_id: data.msg_id,
                conversation_id: data.conversation_id,
                is_reply_msg: data.is_reply_msg,
                msg_body: res_msg[0].msg_body,
                edit_history: res_msg[0].edit_history,
                msg: res_msg[0],
            }));
            break;
        }
        case 'delete_msg': {
            const messagesToDelete = data.other_msg?.length
                ? [...data.other_msg, { msg_id: data.msg_id }]
                : [{ msg_id: data.msg_id }];

            messagesToDelete.forEach((msg) => {
                dispatch(updateOneDiscussionMsg({
                    field: msg.msg_id === data.msg_id ? 'full_delete' : 'has_delete',
                    msg_id: msg.msg_id,
                    conversation_id: data.conversation_id,
                    is_reply_msg: data.is_reply_msg,
                    delete_type: data.delete_type,
                    user_id: data.user_id,
                    reply_for_msgid: data.reply_for_msgid,
                }));
                dispatch(updateOneMsg({
                    field: msg.msg_id === data.msg_id ? 'full_delete' : 'has_delete',
                    msg_id: msg.msg_id,
                    conversation_id: data.conversation_id,
                    is_reply_msg: data.is_reply_msg,
                    delete_type: data.delete_type,
                    user_id: data.user_id,
                    reply_for_msgid: data.reply_for_msgid,
                    task_id: data.task_id,
                }));
            });

            if (data.file_id) {
                dispatch(removeAttachment(data));
            }
            break;
        }
        case 'remove_this_line':
            dispatch(setRemoveThisLine(data));
            break;
        case 'edit_private_participants': {
            await modifyMyMsg([data]);
            const all_users = props.users;
            if (data.secret_user.includes(user_id) || data.sender === user_id) {
                const fieldsToUpdate = [
                    { field: 'secret_user_name', value: getONLYUSERSDATA(all_users, data.secret_user, 'all') },
                    { field: 'secret_user_details', value: getONLYUSERSDATA(all_users, data.secret_user, 'all') },
                    { field: 'secret_user', value: data.secret_user },
                    { field: 'is_secret', value: data.is_secret },
                ];
                fieldsToUpdate.forEach(({ field, value }) => {
                    dispatch(updateOneMsg({
                        field,
                        data,
                        msg_id: data.msg_id,
                        conversation_id: data.conversation_id,
                        is_reply_msg: data.is_reply_msg,
                        [field]: value,
                    }));
                });
            } else {
                dispatch(removeMessage(data));
            }
            dispatch(set_kick_out_data(null));
            break;
        }
        case 'kick_out':
            if (data.kickOut_type !== 'conversation') {
                dispatch(removeMessage(data));
            }
            dispatch(set_kick_out_data(data));
            break;
        case 'add_reac_emoji':
            dispatch(updateOneMsg({
                field: 'has_emoji',
                msg_id: data.msg_id,
                conversation_id: data.conversation_id,
                is_reply_msg: data.is_reply_msg,
                has_emoji: data.result.has_emoji,
            }));
            break;
        case 'flag_unflag':
            // Reserved — handler not yet implemented
            break;
        case 'new_room': {
            const slug = slugify(data.title);
            dispatch(updateConversationMap({ key: slug, value: data.conversation_id }));
            dispatch(add_new_room(data));
            break;
        }
        case 'update_room':
            dispatch(updateRoom(data));
            break;
        case 'read_status_msg':
            dispatch(updateMsgCounter(data));
            break;
        case 'new_notification':
            dispatch(set_dashboardNotification(data));
            break;
        case 'pin_unpin':
            dispatch(set_pin_unpin(data));
            break;
        case 'mute_conversation':
            if (data.type === 'add' || data.type === 'update') {
                dispatch(setMuteDataOnProps({
                    type: data.type,
                    data: data.mute_data,
                    conversation_id: data.conversation_id,
                    active: 'yes',
                }));
            } else {
                dispatch(setMuteDataOnProps({
                    type: data.type,
                    conversation_id: data.conversation_id,
                    active: 'yes',
                }));
            }
            break;
        case 'add_remove_tag_into_msg':
            dispatch(actionTagThunk(data));
            break;
        case 'star_file':
            dispatch(updateMessageStar(data));
            dispatch(updateDiscussionMsgStar(data));
            break;
        case 'delete_link':
            // Reserved — handler not yet implemented
            break;
        case 'delete_one_file':
            dispatch(removeDiscussionAttachment(data));
            dispatch(removeAttachment(data));
            break;
        case 'file_share_link_create':
        case 'file_share_link_remove':
            dispatch(file_share_link_create(data));
            break;
        case 'online_user_lists':
            dispatch(set_online_users(data.list));
            break;
        case 'new_task':
            dispatch(set_new_task(data));
            break;
        case 'task_delete':
            dispatch(set_deleted_task(data));
            break;
        case 'total_task_counter':
            dispatch(update_total_unread(data));
            break;
        case 'save_update_checklist':
            dispatch(save_update_checklist(data));
            break;
        case 'checklist_item_delete':
            dispatch(checklist_item_delete(data));
            break;
        case 'generating_image':
            if (props.user.id === data.sender) {
                dispatch(setPopup({ key: 'generating_image', value: true }));
            }
            break;
        case 'jitsi_ring_send':
            if (Date.now() > parseInt(data.expire_unix)) return;
            dispatch(set_callringpopup({
                status: true,
                user_id,
                user_fullname,
                caller_id: data.user_id,
                caller_name: data.user_fullname,
                caller_img: data.user_img,
                conversation_id: data.conversation_id,
                ring_index: data.ring_index,
                user_busy: data.user_busy,
                call_merge: data.call_merge,
                jwt_token: data.jwt_token,
            }));
            break;
        case 'jitsi_send_hangup':
            dispatch(set_callringpopup({ status: false }));
            break;
        case 'jitsi_send_accept':
            clearTimeout(window.timer1);
            dispatch(set_callringpopup({ status: false }));
            break;
        case 'jitsi_busy_status':
            if (data.voip_busy_conv) dispatch(set_conv_calling_data(data.voip_busy_conv));
            break;
        case 'update_client_version':
            if (data.CLIENT_BASE_URL === 'localhost') return;
            showVersionPopup(data.restart_time, dispatch);
            break;
        case 'eod_report_submit':
            // Reserved — EOD slice handler not yet wired
            break;
        default:
            break;
    }
}
